import React from 'react';
import { Book, Users, Heart, Star, Compass, Sparkles } from 'lucide-react';
import { SeoMeta } from '../components/seo/SeoMeta';

interface CulturalTopic {
  icon: React.ReactNode;
  title: string;
  description: string;
  content: string;
}

export function CulturalInsights() {
  const topics: CulturalTopic[] = [
    {
      icon: <Book className="h-8 w-8 text-indigo-600" />,
      title: "How Chinese Names Work",
      description: "Understanding Chinese name composition and structure",
      content: "Chinese names typically consist of a surname (family name) followed by a given name. Surnames are usually one character, while given names can be one or two characters. The surname comes first, reflecting the cultural emphasis on family identity before individual identity. This structure has remained consistent for thousands of years."
    },
    {
      icon: <Users className="h-8 w-8 text-indigo-600" />,
      title: "Traditional Chinese Naming Customs",
      description: "The role of family and tradition in naming",
      content: "Names often reflect family values and aspirations. Parents choose characters that embody their hopes for their child's future, incorporating virtues like wisdom, kindness, or strength. Some families maintain generation names, where siblings or cousins share a character in their given names, creating a generational poem."
    },
    {
      icon: <Heart className="h-8 w-8 text-indigo-600" />,
      title: "Gender in Chinese Names",
      description: "How gender influences Chinese name selection",
      content: "While Chinese names are generally gender-neutral, certain characters are traditionally associated with masculine or feminine qualities. For example, characters relating to strength or mountains might be used more often in boys' names, while those relating to beauty or grace might be more common in girls' names."
    },
    {
      icon: <Star className="h-8 w-8 text-indigo-600" />,
      title: "Cultural Elements in Chinese Names",
      description: "Traditional elements incorporated in naming",
      content: "Chinese names often incorporate elements from nature, literature, and philosophy. Characters might reference the five elements (wood, fire, earth, metal, water), seasons, virtues from Confucian teachings, or poetic imagery from classical literature. These elements create names with deep cultural resonance."
    },
    {
      icon: <Compass className="h-8 w-8 text-indigo-600" />,
      title: "Regional Naming Variations",
      description: "Geographic influences on Chinese names",
      content: "Naming practices can vary by region and dialect group. Southern Chinese might prefer certain characters over others, while northern regions might have different traditions. Some regions have unique naming customs influenced by local culture and history, creating distinctive regional patterns."
    },
    {
      icon: <Sparkles className="h-8 w-8 text-indigo-600" />,
      title: "Modern Chinese Name Trends",
      description: "Contemporary naming practices and evolution",
      content: "Modern Chinese naming practices often blend tradition with contemporary values. While many parents still choose classical characters, there's a trend toward unique combinations and rarer characters. Some parents also consider international usage when naming children, balancing tradition with global connectivity."
    }
  ];

  return (
    <>
      <SeoMeta
        title="Chinese Name Culture & Traditions | Understanding Chinese Naming Customs"
        description="Confused about how Chinese names work? Learn the deep cultural traditions behind Chinese naming customs. Understand name structure (surname first), gender considerations, generational naming patterns, and why certain characters carry profound meaning. Discover how Chinese names reflect family values, regional variations, and modern trends. Essential reading before choosing your Chinese name."
        keywords="chinese name culture, chinese naming traditions, how do chinese names work, chinese name structure explained, traditional chinese names, chinese name customs, chinese naming conventions, understanding chinese names, chinese name meaning culture, chinese naming practices"
        pathname="/cultural-insights"
      />
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-gray-900">Chinese Name Culture & Traditions</h1>
            <p className="mt-4 text-lg text-gray-500 max-w-3xl mx-auto">
              Discover the rich cultural traditions behind Chinese names and understand the deep significance of Chinese naming customs
            </p>
          </div>

          {/* Understanding Chinese Name Structure Section */}
          <div className="mt-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900">
                Understanding Chinese Name Structure
              </h2>
              <p className="mt-4 text-xl text-gray-600 max-w-4xl mx-auto">
                Learn the fundamental components and organization of Chinese names, from ancient traditions to modern practices
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {topics.slice(0, 3).map((topic, index) => (
                <div
                  key={index}
                  className="bg-white overflow-hidden shadow-lg rounded-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="p-6">
                    <div className="flex items-center justify-center w-12 h-12 rounded-md bg-indigo-50 mb-4">
                      {topic.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {topic.title}
                    </h3>
                    <p className="text-sm text-indigo-600 mb-4">
                      {topic.description}
                    </p>
                    <p className="text-gray-600">
                      {topic.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Traditional Chinese Naming Customs Section */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900">
                Traditional Chinese Naming Customs
              </h2>
              <p className="mt-4 text-xl text-gray-600 max-w-4xl mx-auto">
                Explore time-honored traditions that shape how Chinese names are chosen and their cultural significance
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {topics.slice(3).map((topic, index) => (
                <div
                  key={index}
                  className="bg-white overflow-hidden shadow-lg rounded-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="p-6">
                    <div className="flex items-center justify-center w-12 h-12 rounded-md bg-indigo-50 mb-4">
                      {topic.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {topic.title}
                    </h3>
                    <p className="text-sm text-indigo-600 mb-4">
                      {topic.description}
                    </p>
                    <p className="text-gray-600">
                      {topic.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Modern Chinese Name Trends Section */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900">
                Modern Chinese Name Trends
              </h2>
              <p className="mt-4 text-xl text-gray-600 max-w-4xl mx-auto">
                Discover how Chinese naming practices have evolved in the contemporary era while maintaining cultural roots
              </p>
            </div>

            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Contemporary Influences</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-indigo-600 mr-2">•</span>
                      International considerations for global mobility
                    </li>
                    <li className="flex items-start">
                      <span className="text-indigo-600 mr-2">•</span>
                      Unique character combinations for individuality
                    </li>
                    <li className="flex items-start">
                      <span className="text-indigo-600 mr-2">•</span>
                      Technology-influenced naming patterns
                    </li>
                    <li className="flex items-start">
                      <span className="text-indigo-600 mr-2">•</span>
                      Simplified vs traditional character preferences
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Preservation of Tradition</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      Continued use of classical literature references
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      Maintaining family generation names
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      Respect for auspicious meanings and sounds
                    </li>
                    <li className="flex items-start">
                      <span className="text-purple-600 mr-2">•</span>
                      Integration of five elements theory
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Resources Section */}
          <div className="mt-16">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold text-gray-900">Explore More About Chinese Names</h2>
              <p className="mt-4 text-lg text-gray-500">
                Deepen your understanding of Chinese naming culture with these comprehensive resources
              </p>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <a
                href="/name-meanings"
                className="block p-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg hover:shadow-md transition-shadow"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Chinese Name Meanings
                </h3>
                <p className="text-gray-600">
                  Discover the meanings behind common Chinese characters used in names and their cultural significance.
                </p>
              </a>
              
              <a
                href="/pronunciation"
                className="block p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg hover:shadow-md transition-shadow"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Pronunciation Guide
                </h3>
                <p className="text-gray-600">
                  Learn how to properly pronounce Chinese names with our interactive pronunciation guide and audio examples.
                </p>
              </a>
              
              <a
                href="/chinese-last-names"
                className="block p-6 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg hover:shadow-md transition-shadow"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Chinese Surnames Guide
                </h3>
                <p className="text-gray-600">
                  Explore the origins and meanings of the most common Chinese surnames and their regional distributions.
                </p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
